import { useState } from "react";
import { useInView } from "react-intersection-observer";
import { RouteFactory } from "@app/routePaths";
import logoBTWithTag from "@assets/logo-bt-tag-50.png";
import { BlockTitle } from "@blocks/Blocks";
import Ad from "@components/Advertisement/Ad";
import Container from "@components/Container/Container";
import SeoFooter from "@components/Footer/SeoFooter/SeoFooter";
import Header from "@components/Header/Header";
import HomepageMasthead from "@components/Header/HomepageMasthead";
import HomepageCarouselNewsletter from "@components/Newsletter/HomepageCarouselNewsletter";
import { NEWSLETTER_TYPE } from "@components/Newsletter/types/Newsletter";
import HomeSkybox from "@components/Skybox/HomeSkybox";
import useSendDataToGTM from "@features/analytics/hooks/useSendDataToGTM";
import MyBTCarousel from "@features/mybt/components/Carousel/MyBTCarousel";
import { useUserInfo } from "@features/mySph/hooks/useUserInfo";
import PulseEntry from "@features/pulse/components/PulseEntry/PulseEntry";
import { URL_ORIGIN } from "@features/seo/utils/constants";
import { useHomepageRedirect } from "@hooks/useHomepageRedirect";
import { useInjectClientData } from "@hooks/useInjectClientData";
import SubMobileNavigation from "@particles/Navigation/components/SubMobileNavigation";
import BreakingNews from "@routes/Home/BreakingNews/BreakingNews";
import CompaniesMarkets from "@routes/Home/CompaniesMarkets/CompaniesMarkets";
import Lifestyle from "@routes/Home/Lifestyle/Lifestyle";
import HomepageTopOverview from "@routes/Home/Overview/HomepageTopOverview";
import Podcasts from "@routes/Home/Podcasts/Podcasts";
import { HOMEPAGE_GA_DATA } from "@routes/Home/utils/constants";
import { HomePageData } from "@routes/Home/utils/types";
import Verticals from "@routes/Home/Verticals/Verticals";
import Video from "@routes/Home/Video/Video";
import { USFWebSiteSchema } from "@sphtech/web2-seo-schema";
import { IS_PLAYWRIGHT } from "@util/constant";

import OpinionFeatures from "./OpinionFeatures/OpinionFeatures";

const HomePageContent = ({
  overviewProps,
  breakingNewsProps,
  opinionFeaturesProps,
  seoFooterData,
  companiesMarketsProps,
  verticalsProps,
  podcastProps,
  skyboxProps,
  lifestyle,
  lifestyleCarousel,
  myBTRecommendsData,
  groupedOverviewProps,
}: HomePageData): React.ReactElement => {
  useHomepageRedirect();

  const [myBTVisible, setMyBTVisible] = useState(true);
  const { visitorId } = useUserInfo();
  const sendDataToGTM = useSendDataToGTM();

  const { ref: myBTRef } = useInView({
    threshold: 0,
    onChange: (inView) => {
      if (!inView) return;
      if (myBTVisible) return;

      sendDataToGTM({
        event: "custom_event",
        eventCategory: "mybt",
        eventAction: "visible",
        eventLabel: window.location.href,
      });
      setMyBTVisible(false);
    },
  });

  const handleMyBTClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    const target = e.target as HTMLAnchorElement;
    const label = target.href ? `see more - ${target.href}` : "see more";

    sendDataToGTM({
      event: "custom_event",
      eventCategory: "mybt",
      eventAction: "click",
      eventLabel: label,
    });
  };

  useInjectClientData({ gaProps: HOMEPAGE_GA_DATA });

  return (
    <div className="w-full" data-testid="home-content-component">
      <USFWebSiteSchema
        name="The Business Times"
        url={`${URL_ORIGIN}/`}
        searchUrl="https://www.businesstimes.com.sg/search/?query={search_term_string}"
      />

      <Ad
        adType="lb1"
        className="flex min-h-[calc(50px+0.50rem)] flex-col items-center justify-center border-b border-gray-175 bg-gray-125 py-1 text-center md:py-3 lg:min-h-[calc(250px+1.5rem)]"
      />

      <Header isFromHome isDisplayEditionSwitcher />

      <HomepageMasthead />

      <img
        src={logoBTWithTag}
        alt="business-time-50"
        width={120}
        height={91}
        className="mx-auto my-4 lg:hidden"
      />

      <PulseEntry />

      <Container rootClassName="pt-1 text-gray-850">
        <SubMobileNavigation />

        <HomepageTopOverview
          cardProps={overviewProps}
          groupedCardProps={groupedOverviewProps}
          className="mb-10"
        />

        <HomeSkybox cardProps={skyboxProps} className="mb-10" />

        <BreakingNews
          cardProps={breakingNewsProps}
          className="mb-10 border-t border-black pt-3"
        />

        <>
          {visitorId ? (
            <>
              <div className="mb-3 h-[1px] w-full bg-gray-850"></div>

              <div ref={myBTRef} className="mb-10 w-full pt-1">
                <MyBTCarousel
                  myBTObjects={myBTRecommendsData}
                  title="MYBT"
                  testId="homepage-mybt"
                  refCode="homepage-mybt"
                  header={
                    <BlockTitle
                      text="MYBT"
                      link={RouteFactory.myAccount}
                      onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                        handleMyBTClick(e);
                      }}
                    />
                  }
                />
              </div>
            </>
          ) : null}
        </>

        <div className="mb-3 h-[1px] w-full bg-gray-850"></div>

        <div className="grid lg:grid-cols-3">
          <CompaniesMarkets
            cardProps={companiesMarketsProps}
            className="mb-10 border-gray-175 lg:col-span-2 lg:border-r lg:pr-6"
          />

          <div className="mb-10" data-testid="homepage-st-stocks">
            {IS_PLAYWRIGHT ? (
              <div className="flex h-[537px] w-full items-center justify-center border-gray-700 bg-gray-500 text-gray-700">
                ST INDEX
              </div>
            ) : (
              <iframe
                className="w-full lg:pl-3"
                src="https://sph.shareinvestor.com/bt_stock_chart_frame_2.html"
                title="BT Stock Chart"
                loading="lazy"
                sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox"
                width="100%"
                height={537}
              ></iframe>
            )}
          </div>
        </div>

        <OpinionFeatures
          cardProps={opinionFeaturesProps}
          className={"mb-10 border-t border-black pt-3"}
        />

        <Verticals cardProps={verticalsProps} className="mb-10" />

        <div className="mb-3 h-[1px] w-full bg-gray-850"></div>

        <div className="grid lg:grid-cols-3">
          <Podcasts
            cardProps={podcastProps}
            className="mb-10 border-gray-175 lg:col-span-2 lg:border-r lg:pr-6"
          />

          <div className="mb-10 w-full lg:pl-6">
            <div className="mb-3 block h-[1px] w-full bg-gray-850 lg:hidden"></div>
            <Video testId="homepage-videos" />
          </div>
        </div>

        <div className="mb-3 h-[1px] w-full bg-gray-850"></div>

        <HomepageCarouselNewsletter
          rootClassName="mb-10"
          testId="homepage-carousel-newsletter-component"
          newsletterToSkip={NEWSLETTER_TYPE.BTGLOBAL}
        />
      </Container>

      <div className="mb-10 w-full bg-gray-850 py-10">
        <Container>
          <Lifestyle
            lifestyleCardProps={lifestyle}
            carouselCardProps={lifestyleCarousel}
          />
        </Container>
      </div>

      <SeoFooter data={seoFooterData} />

      <Ad adType="catfish" />
      <Ad adType="prestitial" />
    </div>
  );
};

export default HomePageContent;
